import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import DateRangePicker from '../components/DateRangePicker';
import UsageChart from '../components/UsageChart';
import OrganizationFilter from '../components/OrganizationFilter';
import { useAdminApi } from '../hooks/useAdminApi';
import { TrendResponse, MetricType, TimeBucket } from '../types/adminApiTypes';

const metricOptions: { value: MetricType; label: string }[] = [
  { value: 'total_cost', label: 'Total Costs' },
  { value: 'total_user_messages', label: 'Total User Messages' },
  { value: 'total_users', label: 'Total Users' },
  { value: 'active_users', label: 'Active Users' },
  { value: 'new_users', label: 'New Users' },
];

const timeBucketOptions: { value: TimeBucket; label: string }[] = [
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'daily', label: 'Daily' },
];

const TrendsView: React.FC = () => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>(metricOptions[0].value);
  const [selectedBucket, setSelectedBucket] = useState<TimeBucket>(timeBucketOptions[0].value);
  // Default to 1 year for quarterly view
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(1, 'year'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);

  const { data, loading, error, fetchData } = useAdminApi<TrendResponse>();

  console.log('[TrendsView] Rendering. Current state:', {
    selectedMetric,
    selectedBucket,
    startDate,
    endDate,
  });

  const handleFetchTrends = useCallback(() => {
    console.log('[TrendsView] handleFetchTrends called.');
    if (startDate && endDate && startDate.isValid() && endDate.isValid() && selectedMetric) {
      // Send plain date strings (YYYY-MM-DD). The backend parses these as UTC
      // midnight, avoiding any local-timezone shift from toISOString().
      const params: Record<string, any> = {
        endpoint: `/trends/${selectedMetric}`,
        time_bucket: selectedBucket,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
      };

      // Add organization filter if specific organizations are selected
      // Backend expects repeated query parameters: ?organizations=A&organizations=B
      if (selectedOrganizations.length > 0) {
        params.organizations = selectedOrganizations;
      }

      console.log('[TrendsView] Fetching data with params:', params);
      fetchData(params);
    } else {
      console.log('[TrendsView] Skipping fetch - invalid dates or missing params.');
      if (startDate && !startDate.isValid()) console.warn('[TrendsView] Invalid Start Date');
      if (endDate && !endDate.isValid()) console.warn('[TrendsView] Invalid End Date');
    }
  }, [startDate, endDate, selectedMetric, selectedBucket, selectedOrganizations, fetchData]);

  // Fetch data initially and whenever parameters change
  useEffect(() => {
    console.log('[TrendsView] useEffect triggered due to param change.');
    handleFetchTrends();
  }, [handleFetchTrends]);

  const handleMetricChange = (event: SelectChangeEvent<MetricType>) => {
    console.log('[TrendsView] Metric changed:', event.target.value);
    const newMetric = event.target.value as MetricType;
    setSelectedMetric(newMetric);

    // Auto-adjust time bucket if switching to total_cost with invalid bucket
    if (newMetric === 'total_cost' && (selectedBucket === 'weekly' || selectedBucket === 'daily')) {
      setSelectedBucket('monthly'); // Default to monthly as a reasonable fallback
    }
  };

  const handleBucketChange = (event: SelectChangeEvent<TimeBucket>) => {
    console.log('[TrendsView] Bucket changed:', event.target.value);
    setSelectedBucket(event.target.value as TimeBucket);
  };

  // Add logs for API hook results
  useEffect(() => {
    console.log('[TrendsView] API Hook state update:', { loading, error, data: !!data });
  }, [loading, error, data]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" gutterBottom component="div">
        Trends
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Metric Select */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="metric-select-label">Metric</InputLabel>
              <Select
                labelId="metric-select-label"
                id="metric-select"
                value={selectedMetric}
                label="Metric"
                onChange={handleMetricChange}
              >
                {metricOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Organization Filter */}
          <Grid item xs={12} sm={12} md={5} lg={3}>
            <OrganizationFilter value={selectedOrganizations} onChange={setSelectedOrganizations} />
          </Grid>

          {/* Time Bucket Select */}
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="bucket-select-label">Time Bucket</InputLabel>
              <Select
                labelId="bucket-select-label"
                id="bucket-select"
                value={selectedBucket}
                label="Time Bucket"
                onChange={handleBucketChange}
              >
                {timeBucketOptions.map(option => {
                  // Disable weekly and daily for total_cost metric
                  const isDisabled =
                    selectedMetric === 'total_cost' &&
                    (option.value === 'weekly' || option.value === 'daily');

                  return (
                    <MenuItem key={option.value} value={option.value} disabled={isDisabled}>
                      {option.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range Picker - spans remaining space */}
          <Grid item xs={12} sm={12} md={12} lg={5}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">Error loading trend data: {error}</Alert>}
      {data && !loading && !error && (
        <UsageChart
          data={data.data}
          metricLabel={metricOptions.find(opt => opt.value === selectedMetric)?.label || 'Count'}
          timeBucket={selectedBucket}
        />
      )}
    </Box>
  );
};

export default TrendsView;
